import type { ExplorerOption, GeographyLevel, MetricDefinition, MetricId, StatisticId } from "../types";

export const metricCatalogue: readonly MetricDefinition[] = [
  {
    id: "average-house-price",
    label: "Average House Price",
    description: "Average price paid for residential property, published monthly by the ONS UK House Price Index.",
    unit: "currency-gbp",
    supportedStatistics: ["mean", "median"],
    sourceLabel: "ONS UK House Price Index",
  },
  {
    id: "median-annual-pay",
    label: "Median Annual Pay",
    description: "Gross annual earnings for workplace-based full-time employees, before deductions.",
    unit: "currency-gbp",
    supportedStatistics: ["median", "mean"],
    sourceLabel: "ONS ASHE Table 7",
  },
  {
    id: "mean-annual-pay",
    label: "Mean Annual Pay",
    description: "Mean average gross annual workplace earnings for full-time employees.",
    unit: "currency-gbp",
    supportedStatistics: ["mean", "median"],
    sourceLabel: "ONS ASHE Table 7",
  },
  {
    id: "weekly-pay",
    label: "Gross Weekly Pay",
    description: "Median gross weekly earnings for workplace-based full-time employees.",
    unit: "currency-gbp",
    supportedStatistics: ["median", "mean"],
    sourceLabel: "ONS ASHE Table 7",
  },
  {
    id: "housing-affordability-ratio",
    label: "Housing Affordability Ratio",
    description: "Ratio of median house prices to median workplace-based annual gross earnings.",
    unit: "ratio",
    supportedStatistics: ["median"],
    sourceLabel: "ONS Housing Affordability Ratio",
  },
  {
    id: "employment-rate",
    label: "Employment Rate",
    description: "Percentage of residents aged 16-64 in active employment.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "ONS Annual Population Survey",
  },

  {
    id: "population",
    label: "Population Estimate",
    description: "Usual resident population estimate per Local Authority.",
    unit: "count",
    supportedStatistics: ["count"],
    sourceLabel: "ONS Mid-Year Population Estimates",
  },
  {
    id: "gdhi-per-head",
    label: "Gross Disposable Income (GDHI)",
    description: "Gross disposable household income per head of resident population.",
    unit: "currency-gbp",
    supportedStatistics: ["mean", "median"],
    sourceLabel: "ONS Regional Accounts (GDHI)",
  },
  {
    id: "gva-per-head",
    label: "Gross Value Added (GVA)",
    description: "Gross Value Added per resident, measuring overall local economic output.",
    unit: "currency-gbp",
    supportedStatistics: ["mean", "median"],
    sourceLabel: "ONS Regional Economic Activity",
  },
  {
    id: "claimant-count-rate",
    label: "Claimant Count Rate",
    description: "Percentage of economically active residents claiming jobseekers benefits or Universal Credit.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "ONS & Nomis Monthly Claimant Count",
  },
  {
    id: "co2-emissions-per-capita",
    label: "CO₂ Emissions per Capita",
    description: "Total territorial greenhouse gas emissions in tonnes of CO₂ equivalent per resident.",
    unit: "tonnes CO₂e",
    supportedStatistics: ["rate"],
    sourceLabel: "DESNZ UK Local Authority GHG Statistics",
  },
  {
    id: "ev-charger-density",
    label: "Public EV Charger Density",
    description: "Total public electric vehicle charging devices per 100,000 resident population.",
    unit: "count",
    supportedStatistics: ["count", "mean"],
    sourceLabel: "Department for Transport EV Statistics",
  },
  {
    id: "nvq4-qualifications-rate",
    label: "Qualified to Level 3+",
    description: "Percentage of working-age population (16-64) qualified to Level 3 or above (A-level equivalent).",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "ONS Annual Population Survey",
  },
  {
    id: "life-expectancy",
    label: "Life Expectancy at Birth",
    description: "Estimated average life expectancy at birth in years (England only).",
    unit: "years",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "OHID Fingertips & ONS Life Expectancy",
  },
  {
    id: "gdp-per-head",
    label: "GDP per Head",
    description:
      "Gross domestic product per head at current market prices, measuring local economic output per resident.",
    unit: "currency-gbp",
    supportedStatistics: ["mean", "median"],
    sourceLabel: "ONS Regional Gross Domestic Product",
  },
  {
    id: "business-demography",
    label: "Business Birth Rate",
    description: "Rate of new business births per 100 active businesses, a measure of local business dynamism.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "ONS Business Demography",
  },
  {
    id: "modelled-unemployment",
    label: "Modelled Unemployment Rate",
    description: "Model-based estimate of the local unemployment rate.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "ONS Modelled Unemployment",
  },
  {
    id: "fuel-poverty",
    label: "Fuel Poverty",
    description: "Percentage of households in fuel poverty (England only).",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "DESNZ Fuel Poverty",
  },
  {
    id: "children-in-relative-poverty",
    label: "Children in Relative Poverty",
    description: "Percentage of children in relative poverty after housing costs (England only).",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "DWP Children in Relative Poverty",
  },
  {
    id: "net-additions-to-the-housing-stock",
    label: "Net Housing Stock Additions",
    description: "Net additional dwellings as a percentage of housing stock (England only).",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "MHCLG Net Additions to Housing Stock",
  },
  {
    id: "gigabit-capable-broadband",
    label: "Gigabit-Capable Broadband",
    description: "Percentage of premises with gigabit-capable broadband coverage.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "Ofcom Connected Nations",
  },
  {
    id: "network-coverage-5g",
    label: "5G Network Coverage",
    description: "Percentage of premises with 5G network coverage.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "Ofcom Connected Nations",
  },
  {
    id: "adult-obesity-prevalence",
    label: "Adult Obesity Prevalence",
    description: "Percentage of adults living with obesity (England only).",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "OHID Adult Obesity Prevalence",
  },
  {
    id: "personal-wellbeing",
    label: "Personal Wellbeing (Satisfaction)",
    description: "Mean life satisfaction score out of 10.",
    unit: "score-10",
    supportedStatistics: ["mean", "median"],
    sourceLabel: "ONS Personal Wellbeing",
  },
  {
    id: "first-time-buyer-mortgage-sales",
    label: "First-Time Buyer Mortgage Sales",
    description: "First-time buyer mortgage completions as a percentage of dwelling stock.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "FCA First-Time Buyer Mortgage Sales",
  },
  {
    id: "car-commute-time",
    label: "Drive-to-Work Travel Time",
    description: "Average commute time in minutes for car journeys to work.",
    unit: "min",
    supportedStatistics: ["mean", "median"],
    sourceLabel: "ONS Average Travel Time",
  },
  {
    id: "gcse-attainment",
    label: "GCSE Pass Rate (English & Maths)",
    description: "Percentage of pupils achieving GCSEs in English and Maths.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "DfE GCSEs in English and Maths",
  },
  {
    id: "pupils-at-expected-standard",
    label: "Pupils Meeting Expected Standard",
    description: "Percentage of pupils reaching the expected standard in reading, writing and maths at primary school.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "DfE Primary Attainment",
  },
  {
    id: "apprenticeship-starts",
    label: "Apprenticeship Starts",
    description: "Apprenticeship starts as a percentage of the population (England).",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "DfE Apprenticeships",
  },
  {
    id: "fe-learner-achievements",
    label: "Further Education Achievements",
    description: "Further education and skills learner achievements as a percentage of the population.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "DfE Further Education & Skills",
  },
  {
    id: "fe-participation",
    label: "Further Education Participation",
    description: "Further education and skills participation as a percentage of the population.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "DfE Further Education & Skills",
  },
  {
    id: "no-qualifications",
    label: "No Qualifications",
    description: "Percentage of working-age population with no qualifications.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "ONS APS Qualifications",
  },
  {
    id: "persistent-absences-all",
    label: "Persistent School Absence",
    description: "Percentage of all pupils classed as persistently absent.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "DfE School Attendance",
  },
  {
    id: "persistent-absences-fsm",
    label: "Persistent Absence (FSM Pupils)",
    description: "Percentage of pupils eligible for free school meals classed as persistently absent.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "DfE School Attendance",
  },
  {
    id: "child-obesity",
    label: "Child Obesity (Year 6)",
    description: "Percentage of children aged 10-11 living with obesity (England only).",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "OHID National Child Measurement Programme",
  },
  {
    id: "adult-smokers",
    label: "Adult Smoking Prevalence",
    description: "Percentage of adults who smoke cigarettes.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "OHID Adult Smoking Prevalence",
  },
  {
    id: "early-years-language",
    label: "Early Years Communication",
    description:
      "Percentage of children achieving the expected communication and language level at Early Years Foundation Stage.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "DfE Early Years Foundation Stage",
  },
  {
    id: "cardiovascular-mortality",
    label: "Preventable CVD Mortality",
    description:
      "Age-standardised preventable cardiovascular mortality as a percentage of the population (England only).",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "ONS Preventable CVD Mortality",
  },
  {
    id: "electricity-consumption",
    label: "Domestic Electricity Consumption",
    description: "Average domestic electricity consumption in kWh per meter point.",
    unit: "kWh",
    supportedStatistics: ["mean", "median"],
    sourceLabel: "DESNZ Domestic Electricity Consumption",
  },
  {
    id: "gas-consumption",
    label: "Domestic Gas Consumption",
    description: "Average domestic gas consumption in kWh per meter point.",
    unit: "kWh",
    supportedStatistics: ["mean", "median"],
    sourceLabel: "DESNZ Domestic Gas Consumption",
  },
  {
    id: "coverage-4g",
    label: "4G Network Coverage",
    description: "Percentage of premises with 4G network coverage.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "Ofcom Connected Nations",
  },
  {
    id: "premises-below-30mbps",
    label: "Premises Below 30Mbps",
    description: "Percentage of premises without broadband speeds above 30Mbps.",
    unit: "percentage",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "Ofcom Connected Nations",
  },
  {
    id: "population-density",
    label: "Population Density",
    description: "Residents per square kilometre.",
    unit: "per km²",
    supportedStatistics: ["rate", "mean"],
    sourceLabel: "ONS Population Indicators",
  },
] as const;

export const metricOptions: readonly ExplorerOption<MetricId>[] = metricCatalogue.map(({ id, label }) => ({
  label,
  value: id,
}));

export const geographyOptions: readonly ExplorerOption<GeographyLevel>[] = [
  { label: "Local authority", value: "local-authority" },
  { label: "County", value: "county" },
  { label: "Region", value: "region" },
];

export const statisticOptions: readonly ExplorerOption<StatisticId>[] = [
  { label: "Median", value: "median" },
  { label: "Mean", value: "mean" },
  { label: "Count", value: "count" },
  { label: "Rate", value: "rate" },
];
